/**
 * 中断と再開
 *
 * 1局は30分かかる。**画面を閉じたら消える**というのは、
 * スマホで遊ぶ以上あってはならない — 電話が来ただけで局が飛ぶ。
 *
 * 【状態そのものを保存する。手順の記録ではない】
 * 種と手の列から再現する道もあるが、それは「再現できているか」を
 * 別途確かめ続けなければならない。状態はもともと直列化できる形に
 * してある（`core/state.ts` の不変条件・`PendingDecision` に関数を
 * 持たせない理由がこれ）ので、そのまま書き出すのが素直で確実である。
 *
 * 【乱数だけは作り直す】
 * `FastRng` は method を持つので JSON を往復すると死ぬ。
 * 内部状態（4語）だけを書き出して、読むときに組み直す。
 * `workers/room` の `reviveRoom` と同じ手口。
 *
 * 【壊れた保存で遊べなくなってはいけない】
 * 版が上がって形が変わることもあれば、書き込みの途中で閉じられることもある。
 * 読めなければ**黙って捨てて新しい局を始める**。
 * 保存を読むために遊べない、が一番まずい。
 */

import { FastRng, type GameState, type RngState } from '@tenka/sim';
import type { Fired, Play, Seat } from './game';

const KEY = 'tenka:board:v1';

/**
 * 保存の形。**版を持つ。**
 * 形を変えたら上げる。読めない版は捨てる（移行は書かない —
 * 途中の局が1つ消えるのと、移行の誤りで壊れた局が続くのとでは、前者がまし）。
 */
interface Saved {
  readonly v: 1;
  readonly state: GameState;
  readonly seats: readonly Seat[];
  readonly log: readonly string[];
  readonly waitingFor: number | null;
  readonly rng: RngState;
  readonly wagersEnabled: boolean;
  readonly spotlight: string | null;
  /** この局で起きた史実。決算の「今回起きた出来事」に使う */
  readonly chronicle: readonly Fired[];
  /** 書いた時刻。再開してよいものか人が判断する材料にする */
  readonly at: number;
}

const store = (): Storage | null => {
  try {
    // プライベートモードや設定次第で localStorage 自体が投げる
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

/** 局を書き出す。書けなくても遊びは続く（容量超過など） */
export function save(play: Play): void {
  const s = store();
  if (!s) return;
  const body: Saved = {
    v: 1,
    state: play.state,
    seats: play.seats,
    // 記録は末尾だけ。全部書くと数百行になり、容量の心配が出る
    log: play.log.slice(-60),
    waitingFor: play.waitingFor,
    rng: play.rng.snapshot(),
    wagersEnabled: play.wagersEnabled,
    spotlight: play.spotlight,
    chronicle: play.chronicle,
    at: Date.now(),
  };
  try {
    s.setItem(KEY, JSON.stringify(body));
  } catch {
    // 容量超過。保存できないだけで、遊びは止めない
  }
}

/** 保存を消す。局が終わったときと、読めなかったとき */
export function clear(): void {
  try {
    store()?.removeItem(KEY);
  } catch {
    // 消せなくても続ける
  }
}

/** 保存の中身をざっと確かめる。**信用しない** */
function looksValid(x: unknown): x is Saved {
  if (!x || typeof x !== 'object') return false;
  const s = x as Partial<Saved>;
  if (s.v !== 1) return false;
  if (!s.state || typeof s.state !== 'object') return false;
  if (!Array.isArray(s.seats) || s.seats.length === 0) return false;
  if (!Array.isArray(s.rng) || s.rng.length !== 4) return false;
  if (!Array.isArray(s.state.players) || s.state.players.length !== s.seats.length) return false;
  if (typeof s.state.phase !== 'string') return false;
  return true;
}

/** 保存があれば読む。壊れていれば捨てて null */
export function load(): Play | null {
  const s = store();
  if (!s) return null;
  let raw: string | null = null;
  try {
    raw = s.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!looksValid(parsed)) {
      clear();
      return null;
    }
    return {
      state: parsed.state,
      seats: parsed.seats,
      log: parsed.log ?? [],
      waitingFor: parsed.waitingFor,
      // method を持つので作り直す。書き出したのは内部状態だけ
      rng: new FastRng(parsed.rng),
      wagersEnabled: parsed.wagersEnabled ?? true,
      spotlight: parsed.spotlight ?? null,
      // 古い保存には無い。年表が空になるだけで、遊びは続く
      chronicle: parsed.chronicle ?? [],
    };
  } catch {
    clear();
    return null;
  }
}

/** 保存の時刻。無ければ null。「途中の局がある」と出すのに使う */
export function savedAt(): number | null {
  const s = store();
  if (!s) return null;
  try {
    const raw = s.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return looksValid(parsed) ? parsed.at : null;
  } catch {
    return null;
  }
}
